import { attachFile, queueChatMessage } from '@reducers/chatMessageQueue';
import { loadedImagesErrors } from '@src/actions/common';
import { MediaType, MessagingProvider } from '@src/model/frontendmodel';
import store from '@src/store/reduxstore';
import { ChatTray } from '@src/types/chats';
import mediaUtils, {
  compressImage,
  getImageSizeLimitsB2Chat,
} from '@src/utils/mediaUtils';
import {
  isImageSizeAllowed,
  isImageTypeAllowed,
  isVideoTypeAllowed,
} from '@utils/mediaUtils';

interface IAttachFilesUtil {
  files: File[];
  activeChat: ChatTray;
  formatMessage: (
    message: { id: string },
    values: { name: string; size?: string; imageType?: string }
  ) => string;
  isAttachFilesSummaryEnabled: () => boolean;
}

const attachFilesUtil = ({
  files,
  activeChat,
  formatMessage,
  isAttachFilesSummaryEnabled,
}: IAttachFilesUtil) => {
  const onValidateImages = (fileList: File[]) => {
    const { provider } = activeChat;

    const filesArr = Array.from(fileList);

    const imagesAllowed = filesArr.filter(
      file =>
        (isImageTypeAllowed(file.type) &&
          isImageSizeAllowed(provider, file.size)) ||
        isVideoTypeAllowed(file.type)
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (filesArr.length !== imagesAllowed) {
      filesArr.forEach(file => {
        if (mediaUtils.mimeTypeToMediaType(file.type) === MediaType.IMAGE) {
          if (!isImageTypeAllowed(file.type)) {
            const notAllowedFileMsg = formatMessage(
              { id: 'AgentChatTimeline.OutGoing.SendImg.NoAllowed' },
              { name: file.name, imageType: file.type.split('/')[1] }
            );
            store.dispatch(
              loadedImagesErrors({
                file,
                notAllowedFileMsg,
                maxSizeMB: null,
                compressMsg: null,
              })
            );
          } else if (!isImageSizeAllowed(provider, file.size)) {
            const notAllowedFileMsg = formatMessage(
              { id: 'AgentChatTimeline.OutGoing.SendImg.NoAllowedSizeB2Chat' },
              { name: file.name, size: getImageSizeLimitsB2Chat(provider) }
            );
            store.dispatch(
              loadedImagesErrors({
                file,
                notAllowedFileMsg,
                maxSizeMB: null,
                compressMsg: null,
              })
            );
          }
        }
      });
    }

    return imagesAllowed;
  };

  const images = onValidateImages(files);

  const handleCompressImage = (file: File) =>
    new Promise((resolve, reject) => {
      // eslint-disable-next-line no-new
      compressImage(file)
        .then(compressFile => resolve(compressFile))
        .catch(err => {
          const compressMsg = formatMessage(
            { id: 'AgentChatTimeline.OutGoing.SendImg.CompressError' },
            { name: file.name }
          );

          reject(err);
          store.dispatch(
            loadedImagesErrors({
              file,
              compressMsg,
              maxSizeMB: null,
              notAllowedFileMsg: null,
            })
          );
        });
    });

  if (!activeChat?.id) return;

  const rest = [...files].filter(
    file =>
      ![MediaType.IMAGE, MediaType.VIDEO].includes(
        mediaUtils.mimeTypeToMediaType(file.type)
      )
  );

  if (
    images.length === 1 &&
    activeChat?.provider !== MessagingProvider.WHATSAPPB2CHAT
  ) {
    handleCompressImage(images[0]).then(file => {
      store.dispatch(
        queueChatMessage(
          { type: MediaType.FILE, file: file as File, url: '' },
          activeChat.id,
          activeChat?.provider
        )
      );
    });
  } else {
    images.forEach((img: File) => {
      if (isAttachFilesSummaryEnabled()) {
        handleCompressImage(img).then(file => {
          store.dispatch(
            attachFile(
              { type: MediaType.FILE, file: file as File, url: '' },
              activeChat.id,
              activeChat?.provider
            )
          );
        });
      } else {
        handleCompressImage(img).then(file => {
          store.dispatch(
            queueChatMessage(
              { type: MediaType.FILE, file: file as File, url: '' },
              activeChat.id,
              activeChat?.provider
            )
          );
        });
      }
    });
  }
  rest.forEach(file =>
    store.dispatch(
      queueChatMessage(
        { type: MediaType.FILE, file: file as File, url: '' },
        activeChat.id,
        activeChat?.provider
      )
    )
  );
};

export default attachFilesUtil;
